/**
 * The results screen: the plate, and the tray docked to its bottom edge.
 *
 * Blockari's `GameHost` + `results-scene`, in the kit's markup. The plate is a
 * DOM card (Blockari draws it on the canvas) laid out from `RESULTS_METRICS`
 * in fractions of the board unit `B`, and the tray — the XP beat, the primary,
 * the two secondaries, Home — is docked to the plate's bottom edge with the
 * plate's own radius, so the two read as one object.
 *
 * Deliberately one screen for a solo round and a challenge: it is the same
 * plate throughout — same score, same breakdown — and the only thing that
 * changes is the hero above the rows, which switches from the end reason to
 * the outcome when the server's verdict lands.
 */

import { gsap } from 'gsap';

import { motion } from '@/core/tokens';
import { ROUND_SECONDS, type FinalScore } from '@/game/types';
import { RANK_SPAN, rankFor, tierNumeral, xpLevelFor, xpThreshold } from '@/game/xp';
import type { RankView, XpGained } from '@/net/api';
import { dur, el, formatClock, formatScore, prefersReducedMotion } from '@/ui/hud';
import {
  boardLayout,
  endReasonCopy,
  MIN_READABLE_B,
  outcomeCopy,
  PANEL_TOP_MARGIN_PX,
  resultsFit,
  resultsPanelRect,
  RESULTS_METRICS,
  shortLayout,
  TRAY_CLEARANCE_PX,
  type Fit,
  type HeroKind,
} from '@/ui/results-layout';
import { coin, odometer, rankBadgeCard } from '@/ui/screens';
import { BeatClock, beatAt, beatPlan } from '@/ceremony/beat';
import { CeremonyHost, LevelFlip } from '@/ceremony/host';
import { RANK_T } from '@/ceremony/timeline';
import type { Quality } from '@/pixi/quality';

export interface GameOverCallbacks {
  /** Play again — a fresh solo deck. */
  onRestart(): void;
  /** Share the result. */
  onShare?: () => void;
  /** Back to the title screen. */
  onHome(): void;
  /** The challenge this round belongs to, in full. */
  onSeeChallenge?: (id: string) => void;
  /** Another from the queue. */
  onTakeAnother?: () => void;
  /** Another of your own. */
  onCreateAnother?: () => void;
  /** The gear: sound and notifications. */
  onSettings?: () => void;
}

export type ResultVerdict =
  | {
      outcome: 'win' | 'loss' | 'draw';
      opponent: string;
      theirScore: number;
      mine: number;
      /** What the win paid, when it paid. */
      payout?: number;
      /** What each side put in. */
      stake?: number;
      theirLevel?: number;
    }
  | { outcome: 'unopposed'; mine: number }
  | { outcome: 'pending' }
  | { outcome: 'offline' }
  | null;

export interface VerdictOptions {
  share?: boolean;
  /** Which seat this player held, for the tray's second button. */
  role?: 'creator' | 'taker';
  challengeId?: string | null;
}

/** What the plate says in place of its rows when the round's breakdown is gone (also its tooltip and, in main, an announcement). */
export const UNAVAILABLE_NOTE = 'Details unavailable \u2014 the round was settled on the server.';

/** The seven rows of the breakdown, and which may give way on a short screen. */
const ROWS = 7;
/** Least important first: cards left, clock used, no bust, perfect game. Never the score, the time bonus, LEVEL REACHED. */
const COLLAPSIBLE = [4, 5, 2, 1] as const;

const rankClass = (index: number): string => `w21-rank-${Math.max(0, Math.min(8, index))}`;

const ICON_X =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"></path></svg>';
const ICON_GEAR =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"></path></svg>';
const ICON_SHARE =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3v12"></path><path d="M8 7l4-4 4 4"></path><path d="M4 13v6a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-6"></path></svg>';
const ICON_CARET =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6"></path></svg>';

/**
 * The game root's chrome — Blockari's `.game-chrome`: ✕ (quit) top-left and
 * ⚙ (settings) top-right, 44 px at (8, 8), above every overlay.
 */
export function gameChrome(onQuit: () => void, onSettings: () => void): HTMLElement {
  const chrome = el('div', 'w21-game-chrome');
  const quit = el('button', 'w21-icon-btn') as HTMLButtonElement;
  quit.type = 'button';
  quit.setAttribute('aria-label', 'Quit');
  quit.innerHTML = ICON_X;
  quit.addEventListener('click', onQuit);
  const right = el('div', 'w21-row');
  const gear = el('button', 'w21-icon-btn') as HTMLButtonElement;
  gear.type = 'button';
  gear.setAttribute('aria-label', 'Settings');
  gear.innerHTML = ICON_GEAR;
  gear.addEventListener('click', onSettings);
  right.append(gear);
  chrome.append(quit, right);
  return chrome;
}

interface PlateRow {
  node: HTMLElement;
  label: HTMLElement;
  value: HTMLElement;
}

export class GameOverScreen {
  readonly root: HTMLElement;
  /** The primary: Play again on a solo round, See challenge on a match. */
  readonly restartButton: HTMLAnchorElement;
  readonly homeButton: HTMLAnchorElement;

  readonly #plate = el('div', 'w21-results-plate w21-rp-plain');
  readonly #eyebrow = el('div', 'w21-rp-eyebrow');
  readonly #title = el('div', 'w21-rp-title');
  readonly #banner = el('div', 'w21-rp-banner');
  readonly #rule = el('i', 'w21-rp-rule');
  readonly #score = el('div', 'w21-rp-score');
  readonly #stake = el('div', 'w21-rp-stake');
  readonly #vs = el('div', 'w21-rp-vs');
  /**
   * The hero lines' inner spans. The plate's children are centred on their
   * line by a CSS `translateY(-50%)`, and gsap writes `transform` outright —
   * so the entrance moves these, never the positioned line itself.
   */
  readonly #in = {
    eyebrow: el('span', 'w21-rp-in'),
    title: el('span', 'w21-rp-in'),
    banner: el('span', 'w21-rp-in'),
    score: el('span', 'w21-rp-in', '0'),
    stake: el('span', 'w21-rp-in'),
    vs: el('span', 'w21-rp-in'),
  };
  readonly #rowsBox = el('div', 'w21-rp-rows');
  readonly #rows: PlateRow[] = [];

  readonly #tray = el('div', 'w21-results-tray w21-docked w21-measuring');
  /** A 1 px marker at the foot of the docked tray: gives a scrolling results screen its full height. */
  readonly #spacer = el('div', 'w21-over-spacer');
  readonly #flow = el('div', 'w21-result-flow');
  readonly #strip = el('div', 'w21-result-outcome');
  readonly #xp = el('div', 'w21-xp-beat');
  readonly #secondary = el('div', 'w21-secondary');
  readonly #second: HTMLAnchorElement;
  readonly #shareButton: HTMLButtonElement;
  /**
   * The balance, centred in the chrome's row — Blockari's `ResultsPill`: the top bar is not on this screen, so a
   * win's coins need a pill to land in. It is in the layout for a challenge's results (so its coin is measurable
   * the instant the first coin leaves) and unseen until a payout is in the air; once shown it stays.
   */
  readonly #pill = el('div', 'w21-flight-pill-slot');
  readonly #pillN = el('b');

  #final: FinalScore | null = null;
  /** An award that arrived before the tray revealed; it counts from the reveal. */
  #pendingXp: XpGained | null = null;
  #revealed = false;
  #verdict: ResultVerdict = null;
  #balance: number | null = null;
  #options: VerdictOptions = {};
  #hero: HeroKind = 'plain';
  #tl: gsap.core.Timeline | null = null;
  #clock: BeatClock | null = null;
  #raf = 0;
  #drawBeat: (() => void) | null = null;
  #beatReady = false;
  /** The quality tier the ceremony renders at. Unset follows the governor. */
  quality: Quality | undefined;
  /** Told when the beat is over, so a rank-up's chest can open. */
  onCeremonyDone: (() => void) | undefined;
  /** What CONTINUE says — "Open chest" once a rank-up has earned one. */
  ceremonyContinueLabel: string | undefined;
  /** A guest's rank is what registering keeps: the rank-up offers "Keep your rank". */
  ceremonyKeep: (() => void) | undefined;
  #resize: (() => void) | null = null;

  constructor(private readonly callbacks: GameOverCallbacks) {
    // The screen is Blockari's `.game-root`: the table shows through around
    // the plate, as their world does. No scrim.
    this.root = el('div', 'w21-screen w21-screen--over');
    this.root.dataset.active = 'false';
    // Out of the layout until a round ends. Inactive screens are hidden by
    // visibility, which keeps their boxes — and this one's plate and tray
    // are laid out at the last round's desk geometry, a 553 px box with
    // buttons in it sitting under every shell screen on a 390 phone.
    this.root.hidden = true;
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-label', 'Round over');

    /* -- the plate --------------------------------------------------------- */
    this.#eyebrow.append(this.#in.eyebrow);
    this.#title.append(this.#in.title);
    this.#banner.append(this.#in.banner);
    this.#score.append(this.#in.score);
    this.#stake.append(this.#in.stake);
    this.#vs.append(this.#in.vs);
    for (let i = 0; i < ROWS; i++) {
      const node = el('div', 'w21-rp-row');
      const label = el('span', 'w21-rp-label');
      const value = el('span', 'w21-rp-value');
      node.append(label, value);
      this.#rowsBox.append(node);
      this.#rows.push({ node, label, value });
    }
    this.#plate.append(this.#title, this.#rule, this.#score, this.#rowsBox);

    /* -- the chrome: quit left, settings right ---------------------------- */
    const chrome = gameChrome(
      () => this.callbacks.onHome(),
      () => this.callbacks.onSettings?.(),
    );

    /* -- the tray ---------------------------------------------------------- */

    this.restartButton = el('a', 'w21-btn w21-primary w21-block w21-lg') as HTMLAnchorElement;
    this.restartButton.href = '#';
    this.restartButton.addEventListener('click', (event) => {
      event.preventDefault();
      if (this.animating) {
        this.skip();
        return;
      }
      const id = this.#options.challengeId;
      if (id && this.callbacks.onSeeChallenge) this.callbacks.onSeeChallenge(id);
      else this.callbacks.onRestart();
    });

    this.#second = el('a', 'w21-btn w21-block') as HTMLAnchorElement;
    this.#second.href = '#';
    this.#second.addEventListener('click', (event) => {
      event.preventDefault();
      const role = this.#options.role;
      if (role === 'taker') this.callbacks.onTakeAnother?.();
      else this.callbacks.onCreateAnother?.();
    });
    this.#shareButton = el('button', 'w21-btn w21-accent w21-block') as HTMLButtonElement;
    this.#shareButton.type = 'button';
    this.#shareButton.innerHTML = ICON_SHARE;
    this.#shareButton.append(document.createTextNode('Share'));
    this.#shareButton.addEventListener('click', () => {
      if (this.#shareButton.getAttribute('aria-busy') === 'true') return;
      this.callbacks.onShare?.();
    });
    this.#secondary.append(this.#second, this.#shareButton);

    this.homeButton = el('a', 'w21-result-home', 'Home') as HTMLAnchorElement;
    this.homeButton.href = '#';
    this.homeButton.addEventListener('click', (event) => {
      event.preventDefault();
      this.callbacks.onHome();
    });

    this.#flow.append(this.restartButton, this.#secondary, this.homeButton);
    this.#tray.append(this.#flow);
    this.#tray.setAttribute('aria-hidden', 'true');
    const overlay = el('div', 'w21-overlay w21-actions-only');
    overlay.append(this.#tray);

    const pill = el('div', 'w21-balance');
    pill.append(coin(), this.#pillN, el('small', undefined, '$CHAIN'));
    this.#pill.append(pill);
    this.#pill.hidden = true;
    this.#pill.setAttribute('aria-hidden', 'true');

    this.#spacer.hidden = true;
    this.#spacer.setAttribute('aria-hidden', 'true');
    this.root.append(this.#plate, this.#pill, chrome, overlay, this.#spacer);

    // Anywhere on the plate fast-forwards the payout.
    this.root.addEventListener('pointerdown', (event) => {
      if (!this.animating) return;
      if (event.target instanceof HTMLElement && event.target.closest('button, a')) return;
      this.skip();
    });
  }

  /* ------------------------------------------------------------ verdict -- */

  /**
   * Say how this round went against whoever else played the deck.
   *
   * `null` is a solo game: the plate keeps the end reason as its headline and
   * the tray offers Play again. A settled outcome becomes the plate's hero —
   * never a strip — and only the waiting states (saving, unopposed, offline)
   * take the strip at the top of the tray.
   */
  setVerdict(verdict: ResultVerdict, options: VerdictOptions = {}): void {
    this.#verdict = verdict;
    this.#options = options;
    this.#paintHero();
    this.#paintTray();
    this.#paintPill();
    this.#dock();
  }

  /** The number in the pill — what the balance display shows, `null` while there is no account to show. */
  setBalance(balance: number | null): void {
    this.#balance = balance;
    if (balance !== null) this.#pillN.textContent = formatScore(balance);
    this.#paintPill();
  }

  /**
   * Bring the pill in, before the first coin of a payout leaves; it stays for the rest of the screen. False when
   * it was in already.
   */
  showPill(): boolean {
    if (this.#pill.classList.contains('w21-on')) return false;
    this.#pill.setAttribute('aria-hidden', 'false');
    this.#pill.classList.add('w21-on');
    return true;
  }

  /** The pill's coin, for a flight to land on. */
  pillCoin(): Element | null {
    return this.#pill.hidden ? null : this.#pill.querySelector('.w21-coin');
  }

  /** Only a challenge's results carry it (a solo round has no payout), and only with a balance to show. */
  #paintPill(): void {
    this.#pill.hidden = this.#verdict === null || this.#balance === null;
  }

  #paintHero(): void {
    const final = this.#final;
    const v = this.#verdict;
    const settled = v !== null && (v.outcome === 'win' || v.outcome === 'loss' || v.outcome === 'draw');
    this.#hero = settled ? 'outcome' : 'plain';
    this.#plate.classList.toggle('w21-rp-outcome', settled);
    this.#plate.classList.toggle('w21-rp-plain', !settled);
    this.#plate.classList.toggle('w21-lost', settled && v.outcome === 'loss');
    this.#plate.classList.toggle('w21-draw', settled && v.outcome === 'draw');

    // Only the hero the plate is showing is in the DOM: the plain one is a
    // title, a rule, the score and the rows; the outcome one adds the eyebrow,
    // the banner, the stake and the vs line. Nothing hidden stays mounted.
    const reason = final ? endReasonCopy(final.reason) : '';
    const lines = settled
      ? [this.#eyebrow, this.#banner, this.#rule, this.#score, this.#stake, this.#vs, this.#rowsBox]
      : [this.#title, this.#rule, this.#score, this.#rowsBox];
    this.#plate.replaceChildren(...lines);

    if (!settled || v === null) {
      this.#in.title.textContent = reason;
      return;
    }
    if (v.outcome === 'draw') {
      this.#in.eyebrow.textContent = reason;
      this.#in.banner.textContent = 'DEAD HEAT';
      this.#in.vs.textContent = `${formatScore(v.mine)} vs ${formatScore(v.theirScore)}`;
      return;
    }
    const copy = outcomeCopy({
      won: v.outcome === 'win',
      chain: v.outcome === 'win' ? (v.payout ?? 0) : (v.stake ?? 0),
      myScore: v.mine,
      theirScore: v.theirScore,
      myLevel: final?.levelReached,
      theirLevel: v.theirLevel,
    });
    this.#in.eyebrow.textContent = reason;
    this.#in.banner.textContent = copy.title;
    this.#in.vs.textContent = copy.vs;
    if (copy.stake === '') this.#stake.remove();
    // The coin only on a win: a loss is the stake in rose, no coin.
    this.#in.stake.replaceChildren();
    if (v.outcome === 'win') this.#in.stake.append(coin());
    this.#in.stake.append(document.createTextNode(copy.stake));
  }

  #paintTray(): void {
    const v = this.#verdict;
    const challenge = v !== null;
    const strip: string | null =
      v === null
        ? null
        : v.outcome === 'pending'
          ? 'Saving…'
          : v.outcome === 'unopposed'
            ? 'Target set — first on this deck'
            : v.outcome === 'offline'
              ? 'Not counted — the scoreboard is unreachable'
              : null;
    this.#strip.className = 'w21-result-outcome w21-waiting';
    if (strip === null) this.#strip.remove();
    else {
      this.#strip.textContent = strip;
      this.#flow.prepend(this.#strip);
    }

    this.restartButton.textContent = challenge && this.#options.challengeId ? 'See challenge' : 'Play again';
    // The second: Take another for a taker, Create another for a creator, New
    // challenge on a solo round.
    this.#second.className = challenge ? 'w21-btn w21-block' : 'w21-btn w21-warm w21-block';
    this.#second.textContent = !challenge
      ? 'New challenge'
      : this.#options.role === 'taker'
        ? 'Take another'
        : 'Create another';
  }

  /** The Share button while the cards render and upload: a spinner and "Sharing". */
  setShareBusy(busy: boolean): void {
    this.#shareButton.innerHTML = busy
      ? '<span class="w21-spinner w21-inline" aria-hidden="true"></span>'
      : ICON_SHARE;
    this.#shareButton.append(document.createTextNode(busy ? 'Sharing' : 'Share'));
    this.#shareButton.setAttribute('aria-busy', String(busy));
    this.#shareButton.disabled = busy;
  }

  /* --------------------------------------------------------------- dock -- */

  /**
   * Put the plate where Blockari puts it and dock the tray to its edge.
   *
   * Measured, not styled: the tray's natural height is read while it is
   * hidden (`.measuring` keeps it laid out), the fit decides how the plate
   * gives way — shift up, tighten the rows, collapse the least important ones,
   * drop the eyebrow, tighten the hero, drop the VS line — and both get their
   * inline geometry from the same numbers.
   */
  #dock(): void {
    if (this.root.dataset.active !== 'true') return;
    const vw = this.root.clientWidth || window.innerWidth;
    const vh = this.root.clientHeight || window.innerHeight;
    const safe = (side: 'top' | 'bottom'): number => {
      const raw = getComputedStyle(this.root).getPropertyValue(`--w21-inset-${side === 'top' ? 't' : 'b'}`);
      const n = Number.parseFloat(raw);
      return Number.isFinite(n) ? Math.max(0, n) : 0;
    };

    // The kit's geometry, unless the viewport is too short for it to be readable (a phone on its side):
    // then the plate keeps a phone's size and the SCREEN scrolls, plate first, tray docked under it.
    const kit = boardLayout(vw, vh);
    const scroll = kit.boardSize < MIN_READABLE_B;
    const L = scroll ? shortLayout(vw, vh) : kit;
    const kitRect = resultsPanelRect(L);
    const rect = scroll ? { ...kitRect, y: safe('top') + PANEL_TOP_MARGIN_PX } : kitRect;
    const B = L.boardSize;
    this.root.classList.toggle('w21-over-scroll', scroll);
    // Beside the plate, past the ✕, when it is a phone on its side and there is room (the top centre would sit on
    // the plate's title): the ✕ takes the first 64 px, the compact pill is ~104 px, and a breath before the plate.
    this.#pill.classList.toggle('w21-side', scroll && rect.x >= 64 + 104 + 8);

    if (scroll) {
      // Measure the tray at the width it will have, so the wrapping of its buttons is counted.
      this.#tray.style.left = `${(rect.x - 0.5).toFixed(2)}px`;
      this.#tray.style.width = `${(rect.w + 1).toFixed(2)}px`;
    }
    const trayH = this.#tray.offsetHeight;
    const fit: Fit = resultsFit({
      // Nothing to squeeze into when the screen scrolls: the plate is laid out at its natural size.
      viewportH: scroll ? Number.MAX_SAFE_INTEGER / 4 : vh,
      safeTop: safe('top'),
      safeBottom: safe('bottom'),
      panelY: rect.y,
      plateH: rect.h,
      B,
      trayH,
      overlap: rect.r,
      hero: this.#hero,
      rows: ROWS,
      collapsible: COLLAPSIBLE,
    });

    const top = rect.y + fit.shift;
    const plate = this.#plate.style;
    plate.left = `${rect.x.toFixed(2)}px`;
    plate.top = `${top.toFixed(2)}px`;
    plate.width = `${rect.w.toFixed(2)}px`;
    plate.height = `${fit.panelH.toFixed(2)}px`;
    plate.setProperty('--B', `${B}px`);
    plate.setProperty('--r', `${rect.r.toFixed(2)}px`);
    plate.setProperty('--inset', `${rect.inset.toFixed(2)}px`);
    plate.setProperty('--k', String(fit.k));
    plate.setProperty('--pitch', `${fit.pitch.toFixed(2)}px`);
    plate.setProperty('--rows-y', `${fit.rowsY.toFixed(2)}px`);
    this.#rows.forEach((row, i) => {
      row.node.hidden = fit.hidden.includes(i);
    });
    if (this.#hero === 'outcome') {
      this.#eyebrow.hidden = !fit.eyebrow;
      this.#vs.hidden = !fit.vs;
    }

    const tray = this.#tray.style;
    tray.left = `${(rect.x - 0.5).toFixed(2)}px`;
    tray.top = `${(top + fit.panelH).toFixed(2)}px`;
    tray.width = `${(rect.w + 1).toFixed(2)}px`;
    tray.setProperty('--dock-r', `${rect.r.toFixed(2)}px`);
    tray.setProperty('--dock-inset', `${rect.inset.toFixed(2)}px`);

    // When the screen scrolls, its scrollable height must reach the tray's foot (and clear the safe area).
    const foot = top + fit.panelH + trayH - rect.r + TRAY_CLEARANCE_PX + safe('bottom');
    this.#spacer.style.top = `${Math.max(0, foot).toFixed(2)}px`;
    this.#spacer.hidden = !scroll;
  }

  /* --------------------------------------------------------------- show -- */

  /** True while the breakdown is still being revealed. */
  get animating(): boolean {
    // `progress()`, not `isActive()`: a timeline built this tick has not
    // started yet and reports inactive, so a skip requested in the same
    // frame it was created — the capture harness does exactly that — did
    // nothing and the frames were shot mid-count.
    return this.#tl !== null && this.#tl.progress() < 1;
  }

  show(final: FinalScore): void {
    this.root.hidden = false;
    this.root.dataset.active = 'true';
    this.#tl?.kill();
    this.#tl = null;
    this.#final = final;
    // A fresh round: the verdict of the last one does not carry.
    this.#verdict = null;
    this.#options = {};
    this.#pill.classList.remove('w21-on');
    this.#pill.setAttribute('aria-hidden', 'true');
    this.#paintPill();
    this.#xp.remove();
    this.#xp.replaceChildren();
    this.#pendingXp = null;
    this.#revealed = false;
    this.#paintHero();
    this.#paintTray();

    const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`;
    const clockUsed = Math.max(0, ROUND_SECONDS - Math.round(final.secondsRemaining));
    const rows: Array<{ label: string; text: string; count: number | null; prefix: string }> = [
      { label: 'ROUND SCORE', text: '', count: final.base, prefix: '' },
      {
        label: 'PERFECT GAME',
        text: final.perfectGame ? '' : '—',
        count: final.perfectGame ? final.perfectGameBonus : null,
        prefix: '+',
      },
      {
        label: `NO BUST${final.busts > 0 ? ` (${plural(final.busts, 'BUST').toUpperCase()})` : ''}`,
        text: final.busts === 0 ? '' : '—',
        count: final.busts === 0 ? final.noBustBonus : null,
        prefix: '+',
      },
      { label: 'TIME BONUS', text: '', count: final.timeBonus, prefix: '+' },
      { label: 'CARDS LEFT', text: String(final.cardsRemaining), count: null, prefix: '' },
      { label: 'CLOCK USED', text: formatClock(clockUsed), count: null, prefix: '' },
      { label: 'LEVEL REACHED', text: `LV ${final.levelReached}`, count: null, prefix: '' },
    ];
    // A round the server settled and no longer held: only the score and the level are real. The rows are not
    // drawn (they would be invented numbers); they keep their boxes (`visibility`, not `hidden`) so the plate
    // does not move, and the first one says why.
    const unavailable = final.detailsUnavailable === true;
    if (unavailable) {
      rows.forEach((row, i) => {
        row.label = i === 0 ? 'DETAILS UNAVAILABLE' : '';
        row.text = i === 0 ? '\u2014' : '';
        row.count = null;
      });
    }
    rows.forEach((row, i) => {
      const slot = this.#rows[i];
      if (!slot) return;
      slot.label.textContent = row.label;
      slot.value.textContent = row.count === null ? row.text : `${row.prefix}0`;
      slot.node.style.visibility = unavailable && i > 0 ? 'hidden' : '';
      if (i === 0) slot.node.title = unavailable ? UNAVAILABLE_NOTE : '';
    });

    // Docked before anything moves, with the tray hidden but laid out, so the
    // plate is already the right height for it.
    this.#tray.classList.add('w21-measuring');
    this.#tray.setAttribute('aria-hidden', 'true');
    this.#dock();
    if (!this.#resize) {
      this.#resize = () => this.#dock();
      window.addEventListener('resize', this.#resize);
    }

    // The tray's reveal is the XP beat's cue (Blockari's `xpGo`): an award
    // that landed while the plate was still counting waits here, so the count
    // is never spent behind a hidden tray.
    const reveal = (): void => {
      this.#tray.classList.remove('w21-measuring');
      this.#tray.setAttribute('aria-hidden', 'false');
      this.#revealed = true;
      const pending = this.#pendingXp;
      this.#pendingXp = null;
      if (pending) this.#countXp(pending);
    };

    if (prefersReducedMotion()) {
      this.#in.score.textContent = formatScore(final.total);
      rows.forEach((row, i) => {
        const slot = this.#rows[i];
        if (slot && row.count !== null) slot.value.textContent = `${row.prefix}${formatScore(row.count)}`;
      });
      gsap.set(this.#rows.map((r) => r.node), { opacity: 1, y: 0 });
      gsap.set(Object.values(this.#in), { opacity: 1, y: 0, scale: 1 });
      gsap.set(this.#rule, { opacity: 1 });
      reveal();
      return;
    }

    const rowNodes = this.#rows.map((r) => r.node);
    gsap.set(rowNodes, { opacity: 0, y: 12 });
    gsap.set([this.#in.title, this.#in.eyebrow, this.#in.banner, this.#in.stake, this.#in.vs], { opacity: 0, y: -8 });
    gsap.set(this.#rule, { opacity: 0 });
    gsap.set(this.#in.score, { opacity: 0, scale: 0.86 });
    this.#in.score.textContent = '0';

    // Blockari's rhythm: the hero lines arrive, the score counts to its total
    // over half a second (SCORE_COUNT_S), the rows follow one beat apart with
    // their values already in, and the tray reveals by about three seconds.
    // The old sequence counted each row into a running total and landed the
    // score at 3.5 s with the tray at six — a player who had just lost was
    // kept from the buttons for the length of the celebration.
    const SCORE_COUNT_S = 0.5;
    const ROW_BEAT_S = 0.22;
    const scoreProxy = { value: 0 };
    const tl = gsap.timeline({
      onComplete: () => {
        this.#in.score.textContent = formatScore(final.total);
        reveal();
      },
    });

    tl.to([this.#in.title, this.#in.eyebrow, this.#in.banner], {
      opacity: 1,
      y: 0,
      duration: dur(0.3),
      ease: 'power2.out',
      stagger: 0.05,
    });
    // Opacity only: the rule is centred by its own transform.
    tl.to(this.#rule, { opacity: 1, duration: dur(0.3), ease: 'power2.out' }, '<0.1');
    tl.to(this.#in.score, { opacity: 1, scale: 1, duration: dur(0.3), ease: 'back.out(1.6)' }, '-=0.15');
    tl.to(
      scoreProxy,
      {
        value: final.total,
        duration: dur(SCORE_COUNT_S),
        ease: 'power2.out',
        onUpdate: () => {
          this.#in.score.textContent = formatScore(scoreProxy.value);
        },
        onComplete: () => {
          this.#in.score.textContent = formatScore(final.total);
        },
      },
      '<',
    );
    // The landing.
    tl.fromTo(
      this.#in.score,
      { scale: 1.12 },
      { scale: 1, duration: dur(motion.settle.duration + 0.1), ease: motion.settle.ease },
      '>-0.05',
    );
    tl.to([this.#in.stake, this.#in.vs], { opacity: 1, y: 0, duration: dur(0.3), ease: 'power2.out' }, '<');

    rows.forEach((row, i) => {
      const slot = this.#rows[i];
      if (!slot) return;
      if (row.count !== null) slot.value.textContent = `${row.prefix}${formatScore(row.count)}`;
      tl.to(slot.node, { opacity: 1, y: 0, duration: dur(0.28), ease: 'power3.out' }, i === 0 ? '>-0.1' : `>-${0.28 - ROW_BEAT_S}`);
    });
    tl.to({}, { duration: dur(0.2) });

    this.#tl = tl;
  }

  /** Jump to the end of the payout sequence. */
  skip(): void {
    this.#tl?.totalProgress(1);
  }

  hide(): void {
    this.root.dataset.active = 'false';
    this.root.hidden = true;
    // Leaving the results takes the ceremony with it: an overlay outliving the
    // screen that started it is a screen the player cannot get out of.
    this.#stopBeat();
    this.#tl?.kill();
    this.#tl = null;
  }

  destroy(): void {
    this.#tl?.kill();
    this.#tl = null;
    this.#stopBeat();
    if (this.#resize) window.removeEventListener('resize', this.#resize);
    gsap.killTweensOf([...Object.values(this.#in), this.#rule, ...this.#rows.map((r) => r.node)]);
    this.root.remove();
  }

  /* ------------------------------------------------------------ XP beat -- */

  /**
   * Hold the ceremony at a phase instead of running it through — a capture
   * seam. `null` is the shipping behaviour.
   */
  holdCeremonyAt: 'crossing' | 'reveal' | null = null;

  #release: (() => void) | null = null;

  /** Let a held ceremony continue. */
  releaseXp(): void {
    const go = this.#release;
    this.#release = null;
    go?.();
  }

  /**
   * The XP beat at the top of the tray — the kit's `XpBeat`: who you are
   * (badge, rank, XP LV chip), the delta with its breakdown behind a caret,
   * and the bar to the next level. Counts over 1.2 s once the plate has
   * settled; a rank crossing swaps the badge and the name at the moment the
   * counted value crosses the threshold.
   *
   * Called separately from `show` because the award arrives from the server
   * after the round ends, while the breakdown is already counting.
   */
  showXp(gained: XpGained): void {
    this.#stopBeat();
    const beat = this.#xp;
    beat.replaceChildren();
    delete beat.dataset.settled;
    beat.className = `w21-xp-beat ${rankClass(gained.rankBefore.index)}`;

    const head = el('div', 'w21-xp-beat-head');
    const who = el('div', 'w21-who');
    let badge = rankBadgeCard(gained.rankBefore, 24);
    const name = el('span', 'w21-rank-name');
    const chip = el('span', `w21-chip w21-xp ${rankClass(gained.rankBefore.index)} w21-sm`);
    const level = el('b');
    chip.append(el('small', undefined, 'XP'), el('span', 'w21-lv', 'LV'), level);
    who.append(badge, name, chip);

    const delta = el('button', 'w21-xp-delta') as HTMLButtonElement;
    delta.type = 'button';
    delta.setAttribute('aria-expanded', 'false');
    delta.setAttribute('aria-label', `${gained.total} XP gained; show the breakdown`);
    const num = el('span', 'w21-num');
    const numN = document.createTextNode('+0');
    num.append(numN, el('small', undefined, 'XP'));
    const caret = el('span', 'w21-caret');
    caret.innerHTML = ICON_CARET;
    delta.append(num, caret);
    head.append(who, delta);

    const parts = el('div', 'w21-xp-parts');
    parts.setAttribute('role', 'list');
    parts.setAttribute('aria-hidden', 'true');
    for (const [label, value] of [
      ['played', gained.parts.played],
      ['score', gained.parts.score],
      ['clears', gained.parts.clears],
      ['levels', gained.parts.levels],
      ['streak', gained.parts.streak],
      ['challenge', gained.parts.challenge],
      ['win', gained.parts.win],
    ] as const) {
      if (value <= 0) continue;
      const part = el('span', undefined, label);
      part.setAttribute('role', 'listitem');
      part.append(el('b', undefined, `+${value}`));
      parts.append(part);
    }
    delta.addEventListener('click', () => {
      const open = !parts.classList.contains('w21-open');
      parts.classList.toggle('w21-open', open);
      parts.setAttribute('aria-hidden', String(!open));
      delta.setAttribute('aria-expanded', String(open));
    });

    const bar = el('div', `w21-xp-bar w21-md ${rankClass(gained.rankBefore.index)}`);
    bar.setAttribute('role', 'progressbar');
    const track = el('div', 'w21-xp-track');
    const fill = el('i', 'w21-xp-fill');
    track.append(fill);
    const legend = el('div', 'w21-xp-legend');
    const nums = el('span', 'w21-xp-nums');
    const have = el('b');
    const ceilingText = document.createTextNode('');
    nums.append(have, ceilingText);
    const next = el('span', 'w21-xp-next');
    legend.append(nums, next);
    bar.append(track, legend);

    beat.append(head, parts, bar);

    const tone = (index: number): void => {
      beat.className = `w21-xp-beat ${rankClass(index)}${beat.classList.contains('w21-rankup') ? ' w21-rankup' : ''}${beat.classList.contains('w21-row-hidden') ? ' w21-row-hidden' : ''}`;
      chip.className = `w21-chip w21-xp ${rankClass(index)} w21-sm`;
      bar.className = `w21-xp-bar w21-md ${rankClass(index)}`;
    };

    const paintRank = (rank: RankView): void => {
      name.textContent = `${rank.name} ${tierNumeral(rank.tier)}`;
      badge.title = name.textContent;
    };

    const paint = (xp: number, counted: number): void => {
      const lvl = xpLevelFor(xp);
      const floor = xpThreshold(lvl);
      const ceiling = xpThreshold(lvl + 1);
      const span = Math.max(1, ceiling - floor);
      const p = Math.min(1, Math.max(0, (xp - floor) / span));
      bar.style.setProperty('--p', String(p));
      const shown = Math.round(xp);
      bar.setAttribute('aria-valuemin', String(floor));
      bar.setAttribute('aria-valuemax', String(ceiling));
      bar.setAttribute('aria-valuenow', String(shown));
      bar.setAttribute('aria-label', `XP level ${lvl}: ${formatScore(shown)} of ${formatScore(ceiling)}`);
      have.textContent = formatScore(shown);
      ceilingText.textContent = ` / ${formatScore(ceiling)} XP`;
      const after = rankFor(lvl + 1);
      next.textContent = `${formatScore(Math.max(0, ceiling - shown))} to ${after.name} ${tierNumeral(after.tier)}`;
      // The level's number is an odometer column per digit, as the kit has it.
      if (level.dataset.level !== String(lvl)) {
        level.replaceChildren(odometer(lvl));
        level.dataset.level = String(lvl);
      }
      numN.textContent = `+${formatScore(Math.round(counted))}`;
    };

    paintRank(gained.rankBefore);
    paint(gained.xpBefore, 0);
    this.#flow.insertBefore(beat, this.restartButton);
    this.#dock();

    const finish = (): void => {
      paint(gained.xpAfter, gained.total);
      paintRank(gained.rankAfter);
      paintBadge(gained.rankAfter);
      tone(gained.rankAfter.index);
      beat.dataset.settled = 'true';
      beat.classList.remove('w21-rankup');
    };

    let shownIndex = gained.rankBefore.index;
    const paintBadge = (rank: RankView): void => {
      if (rank.index === shownIndex) return;
      const next = rankBadgeCard(rank, 24);
      badge.replaceWith(next);
      badge = next;
      shownIndex = rank.index;
    };

    if (prefersReducedMotion()) {
      finish();
      return;
    }

    /*
     * ONE clock.
     *
     * The count, the tray's numbers, the level-up's flip and the full-screen
     * rank-up are all `beatAt(plan, clock.elapsed())` — there is no second
     * timer anywhere in this beat, which is what makes "two ranks on screen at
     * once" unrepresentable rather than a bug to be fixed again. The previous
     * version drove the count with a gsap tween and the ceremony with two
     * `setTimeout`s, and those are three clocks that agree only by luck.
     */
    const plan = beatPlan({
      xpBefore: gained.xpBefore,
      xpAfter: gained.xpAfter,
      levelBefore: gained.levelBefore,
      levelAfter: gained.levelAfter,
    });
    const clock = new BeatClock();
    this.#clock = clock;
    const span = Math.max(1, gained.xpAfter - gained.xpBefore);

    let host: CeremonyHost | null = null;
    let flip: LevelFlip | null = null;
    let closed = false;
    /** The ceremony is built and can draw. */
    let ready = plan.ceremony === null;
    /** The clock is holding AT the crossing, waiting for it. */
    let holding = false;
    /** The level flip has drawn at least once, so it is safe to take down. */
    let flipped = false;

    const arrived = (): void => {
      ready = true;
      this.#beatReady = true;
      if (!holding) return;
      holding = false;
      clock.resume(plan.crossMs);
    };

    /*
     * Stand the ceremony up NOW, hidden.
     *
     * The crossing is half a second into the count and building the thing —
     * baking the rank's plates, bringing up a renderer, compiling the shader —
     * takes longer than that on a phone. Started at the crossing, the level-up
     * flip was routinely still loading when its 1.2 s was over and never
     * appeared at all. So it is built while the count runs and revealed on the
     * frame it belongs to.
     */
    const ceremony = plan.ceremony;
    if (ceremony?.kind === 'rank') {
      void CeremonyHost.open(ceremony, document.body, {
        ...(this.quality === undefined ? {} : { quality: this.quality }),
        hidden: true,
        onContinue: () => closeCeremony(),
        // A tap runs the clock on to CONTINUE rather than cutting: the player
        // who skips still lands on the state they earned.
        onSkip: () => clock.jumpTo(RANK_T.cont[1]),
        ...(this.ceremonyContinueLabel === undefined
          ? {}
          : { continueLabel: this.ceremonyContinueLabel }),
        ...(this.ceremonyKeep === undefined ? {} : { onKeep: this.ceremonyKeep }),
      }).then((opened) => {
        if (closed) opened.destroy();
        else host = opened;
        arrived();
      }).catch((error: unknown) => {
        // A ceremony that cannot be built must not take the results screen
        // down with it: the beat carries on and the tray lands on the new rank.
        console.error('[ceremony] the rank-up could not be opened', error);
        arrived();
      });
    } else if (ceremony?.kind === 'level') {
      void LevelFlip.open(ceremony, badge, {
        ...(this.quality === undefined ? {} : { quality: this.quality }),
        hidden: true,
      }).then((opened) => {
        if (closed) {
          opened.destroy();
          badge.style.visibility = '';
        } else flip = opened;
        arrived();
      }).catch((error: unknown) => {
        console.error('[ceremony] the level-up could not be opened', error);
        badge.style.visibility = '';
        arrived();
      });
    }

    const stop = (): void => {
      if (this.#raf) cancelAnimationFrame(this.#raf);
      this.#raf = 0;
    };

    const closeCeremony = (): void => {
      closed = true;
      host?.destroy();
      host = null;
      badge.style.visibility = '';
      finish();
      stop();
      this.onCeremonyDone?.();
    };

    const frame = (): void => {
      /*
       * If the ceremony is not built yet, the count WAITS for it at the
       * crossing rather than running past it.
       *
       * A phone that takes a second to bake the plates used to play the count
       * through, reach the end, and reveal a ceremony that had already timed
       * out — the ceremony simply never appeared. Holding the one clock at the
       * crossing costs a beat of silence on a slow device and guarantees that
       * what the player is here for actually plays.
       */
      if (!ready && !holding && !clock.isPinned && clock.elapsed() >= plan.crossMs) {
        holding = true;
        clock.pin(plan.crossMs);
      }
      const e = clock.elapsed();
      const state = beatAt(plan, e);
      paint(state.shown.xp, (gained.total * (state.shown.xp - gained.xpBefore)) / span);
      paintRank(state.shown.rank);
      paintBadge(state.shown.rank);
      tone(state.shown.rank.index);

      if (state.rank && !closed) {
        // The tray goes quiet under the veil; the ceremony is the screen now.
        beat.classList.add('w21-rankup');
        host?.frame(state.rank, { have: state.shown.xp, ceiling: state.shown.next });
        // The end state is a still, and it waits for CONTINUE — so stop
        // drawing it. A full-screen shader repainting a frozen frame at 60 Hz
        // until somebody taps is the kind of thing that empties a battery on
        // a screen where nothing is happening.
        if (state.rank.done && host) {
          stop();
          return;
        }
      } else if (state.level) {
        // The badge steps aside only once the card that replaces it is there:
        // a hole where the badge was is worse than no flourish at all.
        if (flip) {
          flipped = true;
          badge.style.visibility = 'hidden';
          flip.frame(state.level);
        }
      } else if (flip && flipped) {
        // Only once it has actually flipped. The flip is built while the count
        // runs, so every frame BEFORE the crossing also lands here — and this
        // branch used to tear down the thing it had just spent the count
        // building, which is why the flip never appeared.
        flip.destroy();
        flip = null;
        badge.style.visibility = '';
      }

      // A rank-up holds its end state until CONTINUE; everything else is over
      // when the beat settles.
      if (state.settled && !state.rank) {
        closed = true;
        finish();
        stop();
        this.onCeremonyDone?.();
        return;
      }
      // A pinned clock is a capture: it draws the frame it was asked for and
      // nothing schedules another.
      if (!clock.isPinned) this.#raf = requestAnimationFrame(frame);
    };

    this.#drawBeat = () => frame();
    if (plan.ceremony === null) this.#beatReady = true;

    const run = (): void => {
      // A capture seam: hold the clock at the crossing or just past the swap.
      if (this.holdCeremonyAt === 'crossing') clock.pin(plan.crossMs);
      else if (this.holdCeremonyAt === 'reveal') clock.pin(plan.crossMs + RANK_T.swap + 240);
      else clock.start();
      this.#release = () => clock.resume(clock.elapsed());
      frame();
    };

    if (this.#revealed) run();
    else {
      this.#pendingXp = gained;
      this.#xpCount = run;
    }
  }

  /**
   * Capture seam: pin the beat's clock at `ms` and draw that one frame.
   *
   * The clips the ceremony is reviewed from are shot off this — the tray, the
   * count and the ceremony are one clock, so pinning it and stepping gives a
   * clip whose rhythm is the code's rather than the capture machine's.
   */
  seekBeat(ms: number): void {
    this.#clock?.pin(ms);
    this.#drawBeat?.();
  }

  /** True once the beat is running and its ceremony (if any) can draw. */
  get beatReady(): boolean {
    return this.#beatReady;
  }

  /** Stop the beat's clock and take down anything it put on screen. */
  #stopBeat(): void {
    if (this.#raf) cancelAnimationFrame(this.#raf);
    this.#raf = 0;
    this.#clock = null;
    this.#drawBeat = null;
    this.#beatReady = false;
    this.#release = null;
    for (const node of document.querySelectorAll('.w21-ceremony, .w21-level-flip')) node.remove();
  }

  #xpCount: (() => void) | null = null;

  /** Start the beat's count — the tray's reveal calls this. */
  #countXp(_gained: XpGained): void {
    const go = this.#xpCount;
    this.#xpCount = null;
    go?.();
  }
}
